import React, {ReactNode} from "react";
import cx from "classnames";
import {AiOutlineDoubleLeft, AiOutlineDoubleRight, AiOutlineLeft, AiOutlineRight} from "react-icons/ai";
import {formatNumber} from "@utils/format";

type PageNavigationProps = {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    className: string;
    onPageChange?: (page: number) => void;
    getPageHref?: (page: number) => string;
};

export function PageNavigation(props: PageNavigationProps) {

    const buttons: {
        label: ReactNode, page: number, enabled: boolean,
    }[] = [{
        label: (<div className="flex flex-row items-center justify-center"><AiOutlineDoubleLeft/> First</div>),
        page: 1,
        enabled: props.currentPage > 1
    }, {
        label: (<div className="flex flex-row items-center justify-center"><AiOutlineLeft/> Prev</div>),
        page: props.currentPage - 1,
        enabled: props.currentPage > 1
    }, {
        label: (<div className="flex flex-row items-center justify-center">Next <AiOutlineRight/></div>),
        page: props.currentPage + 1,
        enabled: props.currentPage < props.totalPages
    }, {
        label: (<div className="flex flex-row items-center justify-center">Last <AiOutlineDoubleRight/></div>),
        page: props.totalPages,
        enabled: props.currentPage < props.totalPages
    },];


    return (
        <nav
            className={cx(
                props.className,
                "flex flex-col items-center justify-center gap-2",
                "rounded-xl border border-gray-200 dark:border-gray-700",
                "bg-white/80 dark:bg-gray-900/40",
                "px-4 py-3 shadow-sm",
            )}
            aria-label="Pagination"
        >
            <div className="flex flex-wrap items-center justify-center gap-2">
                {buttons.map((button, index) => {
                    const href = button.enabled ? props.getPageHref?.(button.page) : undefined;
                    const className = cx(
                        "inline-flex items-center justify-center gap-2",
                        "rounded-md border border-gray-200 dark:border-gray-700",
                        "bg-white dark:bg-gray-800",
                        "px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200",
                        "shadow-sm transition-colors",
                        "hover:bg-gray-50 dark:hover:bg-gray-700",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400",
                        !button.enabled && "opacity-50 cursor-not-allowed",
                    );

                    if (!button.enabled || !href) {
                        return (
                            <span
                                key={index}
                                className={className}
                                aria-disabled="true"
                            >
                                {button.label}
                            </span>
                        );
                    }

                    return (
                        <a
                            key={index}
                            href={href}
                            className={className}
                            onClick={(event) => {
                                if (!props.onPageChange) {
                                    return;
                                }
                                event.preventDefault();
                                props.onPageChange(button.page);
                            }}
                        >
                            {button.label}
                        </a>
                    );
                })}
            </div>
            <div className="pagination-summary text-xs text-gray-500 dark:text-gray-400">
                Page {formatNumber(props.currentPage)} of {formatNumber(props.totalPages)} • {formatNumber(props.totalItems)} items
            </div>
        </nav>
    );
}
